// Lean full-screen fade overlay for the global 401 → /login flow.
//
// Performance contract:
//   * No `backdrop-filter` (no GPU blur per frame).
//   * No infinite pulse / rise keyframes (no idle GPU work).
//   * Only two animations run: a one-shot opacity fade and a small
//     spinner rotation. Both are composited (transform/opacity only),
//     so the main thread stays free.
//   * Style sheet is injected once per page, ~1KB.
//   * Total visible lifetime is ~700ms; no work happens before show()
//     or after the post-hide timer fires.

const OVERLAY_ID = 'oms-session-expiry-overlay'
const STYLE_ID = 'oms-session-expiry-style'
const FADE_IN_MS = 200
const FADE_OUT_MS = 200

const STYLES = `
#${OVERLAY_ID} {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: #f6f8fb;
  font-family: inherit;
  opacity: 0;
  animation: oms-sx-in ${FADE_IN_MS}ms ease forwards;
  will-change: opacity;
}

#${OVERLAY_ID}.is-hiding {
  animation: oms-sx-out ${FADE_OUT_MS}ms ease forwards;
}

.oms-sx-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  padding: 1.6rem 1.85rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.10);
  text-align: center;
  min-width: 280px;
  max-width: 360px;
}

.oms-sx-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 4px 10px rgba(14, 91, 70, 0.22);
  margin-bottom: 0.3rem;
}

.oms-sx-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.oms-sx-msg {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.oms-sx-spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #e5e9f2;
  border-top-color: #16a07a;
  animation: oms-sx-spin 0.7s linear infinite;
  margin-top: 0.4rem;
  will-change: transform;
}

@keyframes oms-sx-in  { to { opacity: 1; } }
@keyframes oms-sx-out { to { opacity: 0; } }
@keyframes oms-sx-spin { to { transform: rotate(360deg); } }

@media (prefers-reduced-motion: reduce) {
  #${OVERLAY_ID},
  #${OVERLAY_ID}.is-hiding {
    animation-duration: 80ms;
  }
  .oms-sx-spinner {
    animation: none;
  }
}
`

// HTML template is reused so we don't re-parse innerHTML on every show.
const CARD_HTML = `
<div class="oms-sx-card">
  <div class="oms-sx-icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  </div>
  <h2 class="oms-sx-title">Session expired</h2>
  <p class="oms-sx-msg">Returning you to the sign-in page&hellip;</p>
  <div class="oms-sx-spinner" aria-hidden="true"></div>
</div>
`

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) {
    return
  }
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = STYLES
  document.head.appendChild(style)
}

export function showSessionExpiryOverlay() {
  if (typeof document === 'undefined') {
    return
  }

  const existing = document.getElementById(OVERLAY_ID)
  if (existing) {
    existing.classList.remove('is-hiding')
    return
  }

  ensureStyle()

  const overlay = document.createElement('div')
  overlay.id = OVERLAY_ID
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'Session expired')
  overlay.innerHTML = CARD_HTML

  document.body.appendChild(overlay)
}

export function hideSessionExpiryOverlay() {
  if (typeof document === 'undefined') {
    return
  }
  const overlay = document.getElementById(OVERLAY_ID)
  if (!overlay) {
    return
  }
  overlay.classList.add('is-hiding')
  setTimeout(() => {
    const stillThere = document.getElementById(OVERLAY_ID)
    if (stillThere && stillThere.classList.contains('is-hiding')) {
      stillThere.remove()
    }
  }, FADE_OUT_MS + 20)
}
